import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import {
  Activity,
  Users,
  Globe,
  Zap,
  Clock,
  TrendingUp,
  MapPin,
  Wifi,
  WifiOff,
  RefreshCw,
  Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveAnalytics = ({ isAdmin = false }) => {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [connectionError, setConnectionError] = useState(null);
  const [usePolling, setUsePolling] = useState(false);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const closeSocketSafely = useCallback((socket, reason) => {
    if (!socket) return;
    try {
      if (socket.readyState === WebSocket.CONNECTING) {
        socket.onopen = () => socket.close(1000, reason);
        socket.onerror = () => {};
      } else if (socket.readyState === WebSocket.OPEN) {
        socket.close(1000, reason);
      }
    } catch { /* Ignore teardown errors. */ }
  }, []);

  const authorization = `Bearer ${token}`;

  // Polling fallback for stats
  const fetchStatsViaAPI = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/advanced`, {
        headers: { Authorization: authorization },
        params: { period: 'daily', days: 1 }
      });
      
      const data = response.data;
      // Calculate redirected from time_series if available
      const redirected = data.time_series?.reduce((sum, item) => sum + (item.redirected || 0), 0) || 0;
      
      setStats({
        total_visits: data.summary.total_visits || 0,
        human_visits: data.summary.human_visits || 0,
        bot_visits: data.summary.bot_visits || 0,
        redirected: redirected,
        recent_count: data.summary.human_visits || 0
      });
      
      // Also get live visitors
      const liveResponse = await axios.get(`${API}/analytics/live-visitors`, { headers: { Authorization: authorization } });
      setRecentVisitors(liveResponse.data.visitors || []);
      
      setIsConnected(true);
      setConnectionError(null);
    } catch (error) {
      console.error('Polling error:', error);
      setConnectionError('Failed to fetch data');
    }
  }, [authorization]);

  const handleMessage = useCallback((message) => {
    switch (message.type) {
      case 'connected':
        break;
      case 'stats_update':
        setStats(message.data);
        if (message.data.recent_visitors) setRecentVisitors(message.data.recent_visitors);
        break;
      case 'platform_stats':
        setStats(message.data);
        break;
      case 'live_visitors':
        setRecentVisitors(message.data.visitors || []);
        break;
      case 'new_visitor':
        setRecentVisitors(prev => [message.data, ...prev.slice(0, 9)]);
        setStats(prev => prev ? ({ ...prev, total_visits: (prev.total_visits || 0) + 1, recent_count: (prev.recent_count || 0) + 1 }) : prev);
        break;
      case 'conversion':
        setStats(prev => prev ? ({ ...prev, redirected: (prev.redirected || 0) + 1 }) : prev);
        break;
      case 'pong':
        break;
      default:
        break;
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!token) return;

    // Close existing connection
    if (wsRef.current) {
      closeSocketSafely(wsRef.current, 'Replacing connection');
    }

    // Create WebSocket URL
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const endpoint = isAdmin ? 'ws/admin-dashboard' : 'ws/live-analytics';
    const wsUrl = `${wsProtocol}://${wsHost}/api/${endpoint}?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Set connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout, falling back to polling');
          closeSocketSafely(ws, 'Connection timeout');
          setUsePolling(true);
        }
      }, 5000);

      ws.onopen = () => {
        if (wsRef.current !== ws) {
          closeSocketSafely(ws, 'Stale connection');
          return;
        }
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        setConnectionError(null);
        setUsePolling(false);
        console.log('WebSocket connected');

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        // Request initial stats
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ 
              type: isAdmin ? 'get_platform_stats' : 'get_stats' 
            }));
          }
        }, 500);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (e) {
          console.error('Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        if (wsRef.current === ws) setIsConnected(false);
        clearInterval(pingIntervalRef.current);

        // If closed with error, try polling
        if (event.code !== 1000 && event.code !== 1005) {
          console.log('WebSocket closed with error, trying polling');
          setUsePolling(true);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        setConnectionError('WebSocket unavailable, using polling');
        setUsePolling(true);
        console.warn('WebSocket unavailable; polling fallback enabled');
      };
    } catch (error) {
      setConnectionError('Failed to connect');
      setUsePolling(true);
      console.error('WebSocket connection error:', error);
    }
  }, [token, isAdmin, closeSocketSafely, handleMessage]);

  const requestStats = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ 
        type: isAdmin ? 'get_platform_stats' : 'get_stats' 
      }));
    }
  };

  useEffect(() => {
    // Try WebSocket first
    connectWebSocket();

    return () => {
      const socket = wsRef.current;
      clearInterval(pingIntervalRef.current);
      clearInterval(pollingIntervalRef.current);
      closeSocketSafely(socket, 'Component unmounting');
      if (wsRef.current === socket) wsRef.current = null;
    };
  }, [connectWebSocket, closeSocketSafely]);

  // Handle polling fallback
  useEffect(() => {
    if (usePolling) {
      // Initial fetch
      fetchStatsViaAPI();
      
      // Set up polling interval
      pollingIntervalRef.current = setInterval(() => {
        fetchStatsViaAPI();
      }, 5000); // Poll every 5 seconds
      
      return () => {
        clearInterval(pollingIntervalRef.current);
      };
    }
  }, [usePolling, fetchStatsViaAPI]);

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'REDIRECTED':
        return 'text-green-400';
      case 'LANDING PAGE':
        return 'text-blue-400';
      case 'OK':
        return 'text-cyan-400';
      default:
        return 'text-red-400';
    }
  };

  return (
    <div className="space-y-4" data-testid="live-analytics">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isConnected ? (
            <>
              <div className="relative">
                <Wifi className="w-5 h-5 text-green-400" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              </div>
              <span className="text-green-400 text-sm font-medium">
                {usePolling ? 'Live (Polling)' : 'Live'}
              </span>
            </>
          ) : (
            <>
              <WifiOff className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm">Connecting...</span>
            </>
          )}
        </div>
        <button
          onClick={usePolling ? fetchStatsViaAPI : requestStats}
          className="btn-ghost p-2"
          title="Refresh stats"
        >
          <RefreshCw className={`w-4 h-4 ${!isConnected ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {connectionError && !isConnected && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
          {connectionError}
        </div>
      )}

      {/* Live Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-dark p-4 border border-cyan-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-slate-400">Total Visits</span>
            </div>
            <p className="text-2xl font-bold text-cyan-400 font-orbitron">
              {stats.total_visits?.toLocaleString() || 0}
            </p>
          </div>

          <div className="card-dark p-4 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-xs text-slate-400">Human</span>
            </div>
            <p className="text-2xl font-bold text-green-400 font-orbitron">
              {stats.human_visits?.toLocaleString() || 0}
            </p>
          </div>

          {isAdmin ? (
            <>
              <div className="card-dark p-4 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-slate-400">Licensed Users</span>
                </div>
                <p className="text-2xl font-bold text-purple-400 font-orbitron">
                  {stats.licensed_users || 0}
                </p>
              </div>

              <div className="card-dark p-4 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-slate-400">Pending</span>
                </div>
                <p className="text-2xl font-bold text-amber-400 font-orbitron">
                  {stats.pending_transactions || 0}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="card-dark p-4 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-slate-400">Redirected</span>
                </div>
                <p className="text-2xl font-bold text-amber-400 font-orbitron">
                  {stats.redirected?.toLocaleString() || 0}
                </p>
              </div>

              <div className="card-dark p-4 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-slate-400">Recent (5m)</span>
                </div>
                <p className="text-2xl font-bold text-purple-400 font-orbitron">
                  {stats.recent_count || 0}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recent Visitors */}
      {recentVisitors.length > 0 && (
        <div className="card-dark p-4">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-cyan-400" />
            <h4 className="text-sm font-semibold text-white">Live Visitors</h4>
            <span className="ml-auto text-xs text-slate-500">Last 5 minutes</span>
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {recentVisitors.map((visitor, idx) => (
              <div
                key={`${visitor.ip}-${visitor.timestamp}-${idx}`}
                className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg text-xs animate-fade-in"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-slate-500" />
                    <span className="text-slate-300">{visitor.country || 'Unknown'}</span>
                  </div>
                  <span className="text-slate-500 font-mono">{visitor.ip}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${getStatusColor(visitor.status)}`}>
                    {visitor.status}
                  </span>
                  <span className="text-slate-600">
                    {new Date(visitor.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Connections */}
      {stats?.active_connections !== undefined && (
        <div className="text-xs text-slate-500 text-center">
          {stats.active_connections} active connection{stats.active_connections !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default LiveAnalytics;
